import { useCallback, useMemo, useState } from 'react';
import { useTheme } from '@emotion/react';
import { observer } from 'mobx-react';
import { Avatar } from 'antd';
import { intl } from 'components/localization';
import OrganizationPlaceholder from 'assets/images/organization-placeholder.svg';
import PopoverTreeSelector from 'components/common/popover-tree-selector/popover-tree-selector';
import CreateOrganizationModal from 'components/organizations/create-organization-modal';
import useStores from 'hooks/use-stores.hooks';
import { useNavigate } from 'react-router';
import styled from 'types/theme.types';
import { getOrganizationName } from 'utils/organizations.utils';
import sortBy from 'lodash/sortBy';
import { useCurrentOrganization } from 'hooks/use-current-organization-id';
import { type Organization } from 'types/api.types';
import PopoverTreeLabel from 'components/layout/topbar/popover-tree-label';

const TopbarOrganizationSelector = () => {
  const { isNewDesign } = useTheme();
  const { organizationsStore } = useStores();
  const organization = useCurrentOrganization();
  const navigate = useNavigate();
  const [isAddOrganizationModalOpen, setIsAddOrganizationModalOpen] = useState(false);

  // Check if organizations are still loading
  const isLoading = !organization || Object.keys(organizationsStore.organizations).length === 0;

  const handleOrganizationSelect = useCallback(
    (selectedKeys: React.Key[]) => {
      const orgId = selectedKeys[0] as string;
      if (orgId && orgId !== organization.id) {
        organizationsStore.switchCurrentOrganization(orgId, undefined, '/');
      }
    },
    [organizationsStore, organization.id]
  );

  const handleOrganizationLabelClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigate('/');
    },
    [navigate]
  );

  const handleAddOrganization = useCallback(() => {
    setIsAddOrganizationModalOpen(true);
  }, []);

  const organizationTreeData = useMemo(() => {
    const allOrganizations = Object.values(organizationsStore.organizations);
    const sortedOrganizations = sortBy(allOrganizations, (org: Organization) => getOrganizationName(org));

    return sortedOrganizations.map((org: Organization) => ({
      key: org.id,
      title: (
        <OrganizationOption>
          <OrganizationAvatar src={org.photoUrl || OrganizationPlaceholder} size={20} />
          <span>{getOrganizationName(org)}</span>
        </OrganizationOption>
      ),
      searchText: getOrganizationName(org),
      selectable: true
    }));
  }, [organizationsStore.organizations]);

  return (
    <>
      <PopoverTreeSelector
        treeData={organizationTreeData}
        selectedKeys={organization?.id ? [organization.id] : []}
        onSelect={handleOrganizationSelect}
        showSearch={true}
        searchPlaceholder={intl.formatMessage({ id: 'topbar.organization-selector.search-placeholder' })}
        sectionLabel={intl.formatMessage({ id: 'topbar.organization-selector.section-label' })}
        footerTitle={intl.formatMessage({
          id: isNewDesign ? 'topbar.organization-selector.add-button.label' : 'topbar.organization-selector.add-button'
        })}
        onFooterClick={handleAddOrganization}
        isLoading={isLoading}
        dataE2e="org-popover-tree-selector"
        trigger={
          <PopoverTreeLabel
            icon={
              <OrganizationAvatar
                src={organization?.photoUrl || OrganizationPlaceholder}
                // Figma's Components/Avatar containerSizeSM. 26 was the pre-restyle size.
                size={isNewDesign ? 24 : 26}
              />
            }
            text={getOrganizationName(organization)}
            onClick={handleOrganizationLabelClick}
            data-e2e="organization-label"
          />
        }
      />
      <CreateOrganizationModal
        open={isAddOrganizationModalOpen}
        hideModal={() => setIsAddOrganizationModalOpen(false)}
      />
    </>
  );
};

const OrganizationOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  overflow: hidden;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }
`;

const OrganizationAvatar = styled(Avatar)`
  flex-shrink: 0;
  margin-bottom: 1px;
`;

export default observer(TopbarOrganizationSelector);
